import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { fmtCurrency } from '../../lib/display-labels';
import PhotoUploader from '../PhotoUploader';
import { X, Loader2, Upload, FileText, Check } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { calendarDayString, formatCalendarDate, todayCalendarDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { toDatetimeLocal } from '../../lib/datetime-local';
import { blurOnWheel } from '../../lib/number-input';
import {
  REPAIR_CAUSES,
  REPAIR_CAUSE_LABELS,
  emptyPart,
  optionalField,
  partsError,
  partsToDrafts,
  serializeParts,
  windowInstant,
  type PartDraft,
} from '../../lib/repair-log';

export interface MaintenanceRecord {
  id: string;
  equipment_id: string;
  unit_number?: string;
  service_date: string;
  maintenance_type: string;
  status: string;
  description: string;
  performed_by?: string;
  vendor?: string;
  meter_reading?: number | null;
  total_cost: number;
  parts_cost?: number;
  labor_cost?: number;
  misc_cost?: number;
  downtime_hours?: number | null;
  next_service_due?: string | null;
  notes?: string;
  receipt_key?: string | null;
  receipt_url?: string | null;
  ocr_raw?: any;
  // The repair log (api#979 / 0492). Snake_case like the rest of this record.
  cause?: string | null;
  failed_component?: string | null;
  parts?: Array<{ part: string; part_number?: string; qty: number; unit_cost?: number }> | null;
  inspection_submission_id?: string | null;
  out_of_service_from?: string | null;
  returned_to_service_at?: string | null;
}

export interface OcrResult {
  vendor: string;
  date: string;
  invoice_ref: string;
  line_items: { description: string; amount: number }[];
  subtotal: number;
  gst: number;
  total: number;
  category_suggestion: string;
  category_reason: string;
}

interface EquipmentUnit {
  id: string;
  unit_number?: string;
  name?: string;
  type?: string;
  status?: string;
}

interface MaintenanceRecordDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create/update, before the dialog closes. */
  onSaved?: () => void;
  /** When set, the equipment selector is hidden and this unit is used. */
  preselectedEquipmentId?: string;
  /** When set, the dialog is in edit mode. */
  record?: MaintenanceRecord | null;
  /** Extra footer controls (e.g. Delete / Download PDF), rendered only in edit mode. */
  renderFooterExtra?: (record: MaintenanceRecord) => React.ReactNode;
  /**
   * "Log repair from this inspection" (create only). Sent as
   * inspection_submission_id; with no description typed, the API fills the
   * description from the inspection's FAILED items and the cause with
   * inspection_finding. Nothing is pre-filled here.
   */
  inspectionSubmissionId?: string;
}

/**
 * Re-exported so the dialog's existing importers keep working. The local body
 * returned '$0.00' for an absent cost — a maintenance record whose price nobody
 * entered read as free work.
 */
export const fmtMoney = fmtCurrency;

/**
 * Maintenance dates are calendar days. The `includes('T')` splice was doing by
 * hand exactly what parseCalendarDate does — split the time off, build the day
 * locally. @see formatCalendarDate.
 */
export const fmtDate = (d: string | null | undefined) =>
  formatCalendarDate(d, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';

export const MAINTENANCE_TYPES = [
  'Preventive',
  'Corrective',
  'Inspection',
  'Repair',
  'Calibration',
  'Overhaul',
  'Other',
];

export const STATUS_OPTIONS = ['scheduled', 'in_progress', 'completed', 'cancelled'];

interface FormState {
  equipment_id: string;
  service_date: string;
  maintenance_type: string;
  status: string;
  description: string;
  performed_by: string;
  vendor: string;
  meter_reading: string;
  total_cost: string;
  parts_cost: string;
  labor_cost: string;
  misc_cost: string;
  downtime_hours: string;
  next_service_due: string;
  notes: string;
  receipt_key: string;
  ocr_raw: any;
  cause: string;
  failed_component: string;
  parts: PartDraft[];
  /** datetime-local strings on the tenant's clock; '' = not recorded. */
  out_of_service_from: string;
  returned_to_service_at: string;
}

// The service-date seed is the TENANT's today; '' (an empty picker) when the
// zone is unknown — an input seed, never a rendered value.
const emptyForm = (timeZone: string | null, equipmentId?: string): FormState => ({
  equipment_id: equipmentId || '',
  service_date: todayCalendarDate(timeZone) ?? '',
  maintenance_type: '',
  status: 'completed',
  description: '',
  performed_by: '',
  vendor: '',
  meter_reading: '',
  total_cost: '',
  parts_cost: '',
  labor_cost: '',
  misc_cost: '',
  downtime_hours: '',
  next_service_due: '',
  notes: '',
  receipt_key: '',
  ocr_raw: null,
  cause: '',
  failed_component: '',
  parts: [],
  out_of_service_from: '',
  returned_to_service_at: '',
});

/* ─── OCR Review Step ─────────────────────────────────────────────── */

function OcrReviewPanel({
  ocrResult,
  onAccept,
  onCancel,
}: {
  ocrResult: OcrResult;
  onAccept: (ocr: OcrResult) => void;
  onCancel: () => void;
}) {
  return (
    <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-white flex items-center gap-2">
          <FileText className="w-4 h-4 text-fieldiq-accent" />
          Receipt OCR Results
        </h4>
        <button onClick={onCancel} className="text-fieldiq-muted hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <span className="text-fieldiq-muted">Vendor</span>
          <p className="text-white font-medium mt-0.5">{ocrResult.vendor || '—'}</p>
        </div>
        <div>
          <span className="text-fieldiq-muted">Date</span>
          <p className="text-white font-medium mt-0.5">{ocrResult.date || '—'}</p>
        </div>
        <div>
          <span className="text-fieldiq-muted">Invoice Ref</span>
          <p className="text-white font-medium mt-0.5">{ocrResult.invoice_ref || '—'}</p>
        </div>
        <div>
          <span className="text-fieldiq-muted">Total</span>
          <p className="text-white font-bold mt-0.5">{fmtMoney(ocrResult.total)}</p>
        </div>
      </div>

      {ocrResult.line_items?.length > 0 && (
        <div>
          <span className="text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider">
            Line Items
          </span>
          <div className="mt-1 space-y-1">
            {ocrResult.line_items.map((li, i) => (
              <div key={i} className="flex justify-between text-xs bg-fieldiq-dark/50 px-3 py-1.5 rounded">
                <span className="text-white">{li.description}</span>
                <span className="text-fieldiq-muted font-medium">{fmtMoney(li.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {ocrResult.category_suggestion && (
        <div className="text-xs">
          <span className="text-fieldiq-muted">Suggested Category: </span>
          <span className="text-fieldiq-accent font-medium">{ocrResult.category_suggestion}</span>
          {ocrResult.category_reason && (
            <span className="text-fieldiq-muted"> — {ocrResult.category_reason}</span>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => onAccept(ocrResult)}
          className="flex-1 flex items-center justify-center gap-2 bg-fieldiq-accent text-fieldiq-dark font-semibold py-2 rounded-lg text-sm hover:bg-amber-400 transition-colors"
        >
          <Check className="w-4 h-4" /> Apply to Form
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-fieldiq-border text-fieldiq-muted rounded-lg text-sm hover:text-white transition-colors"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

/* ─── Dialog ──────────────────────────────────────────────────────── */

export default function MaintenanceRecordDialog({
  open,
  onClose,
  onSaved,
  preselectedEquipmentId,
  record,
  renderFooterExtra,
  inspectionSubmissionId,
}: MaintenanceRecordDialogProps) {
  const queryClient = useQueryClient();
  const { isMetric } = useLocale();
  const distanceUnit = isMetric ? 'km' : 'mi';
  const isEdit = !!record;
  const timeZone = useTenantTimeZone();

  const [form, setForm] = useState<FormState>(emptyForm(timeZone, preselectedEquipmentId));
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState<OcrResult | null>(null);
  const [receiptPhotos, setReceiptPhotos] = useState<{ key: string; url: string }[]>([]);

  useEffect(() => {
    if (!open) return;
    setOcrResult(null);
    if (record) {
      setForm({
        equipment_id: record.equipment_id || preselectedEquipmentId || '',
        service_date: calendarDayString(record.service_date) ?? record.service_date ?? '',
        maintenance_type: record.maintenance_type || '',
        status: record.status || 'completed',
        description: record.description || '',
        performed_by: record.performed_by || '',
        vendor: record.vendor || '',
        meter_reading: record.meter_reading != null ? String(record.meter_reading) : '',
        total_cost: record.total_cost != null ? String(record.total_cost) : '',
        parts_cost: record.parts_cost != null ? String(record.parts_cost) : '',
        labor_cost: record.labor_cost != null ? String(record.labor_cost) : '',
        misc_cost: record.misc_cost != null ? String(record.misc_cost) : '',
        downtime_hours: record.downtime_hours != null ? String(record.downtime_hours) : '',
        next_service_due: calendarDayString(record.next_service_due) ?? record.next_service_due ?? '',
        notes: record.notes || '',
        receipt_key: record.receipt_key || '',
        ocr_raw: record.ocr_raw || null,
        cause: record.cause || '',
        failed_component: record.failed_component || '',
        parts: partsToDrafts(record.parts),
        out_of_service_from: toDatetimeLocal(record.out_of_service_from, timeZone),
        returned_to_service_at: toDatetimeLocal(record.returned_to_service_at, timeZone),
      });
      setReceiptPhotos(record.receipt_key ? [{ key: record.receipt_key, url: record.receipt_url || '' }] : []);
    } else {
      setForm(emptyForm(timeZone, preselectedEquipmentId));
      setReceiptPhotos([]);
    }
  }, [record, preselectedEquipmentId, open, timeZone]);

  const { data: equipmentData } = useQuery<EquipmentUnit[]>({
    queryKey: ['equipment'],
    queryFn: () => api.equipment.list().then((r) => {
      const d = r.data?.data ?? r.data;
      return Array.isArray(d) ? d : [];
    }),
    staleTime: 60_000,
    enabled: open,
  });
  const equipmentList: EquipmentUnit[] = equipmentData ?? [];

  const saveMutation = useMutation({
    mutationFn: (data: any) =>
      isEdit
        ? api.equipmentMaintenance.update(record!.id, data)
        : api.equipmentMaintenance.create(data),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['equipment-maintenance'] });
      toast.success(isEdit ? 'Maintenance record updated' : 'Maintenance record created');
      // The API's own figures and warnings, shown as it sent them — this dialog
      // never totals the parts or measures the window itself.
      const body = res?.data ?? {};
      if (body.downtime_mismatch_warning) toast.warning(body.downtime_mismatch_warning);
      if (body.cost_mismatch_warning) toast.warning(body.cost_mismatch_warning);
      onSaved?.();
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to save record');
    },
  });

  const updateField = (key: keyof FormState, value: string | any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!form.equipment_id) {
      toast.error('Please select an equipment unit');
      return;
    }
    if (!form.service_date) {
      toast.error('Please enter a service date');
      return;
    }

    const partsProblem = partsError(form.parts);
    if (partsProblem) {
      toast.error(partsProblem);
      return;
    }
    const oosFrom = windowInstant(form.out_of_service_from, timeZone);
    const backAt = windowInstant(form.returned_to_service_at, timeZone);
    if ((form.out_of_service_from && !oosFrom) || (form.returned_to_service_at && !backAt)) {
      toast.error('The out-of-service time could not be read — set the company time zone, or re-enter it');
      return;
    }
    if (oosFrom && backAt && Date.parse(backAt) < Date.parse(oosFrom)) {
      toast.error('Returned to service is before out of service');
      return;
    }

    // Window: an edit sends a time only when its picker string MOVED, so a
    // round trip through the picker is not a seconds-dropping "edit"
    // (lib/datetime-local.ts). A cleared picker sends null.
    const windowField = (key: 'out_of_service_from' | 'returned_to_service_at', instant: string | null) => {
      if (!isEdit) return instant ?? undefined;
      const before = toDatetimeLocal(record?.[key] ?? null, timeZone);
      if (form[key] === before) return undefined;
      return instant;
    };
    const parts = serializeParts(form.parts);
    const hadParts = Array.isArray(record?.parts) && record!.parts!.length > 0;

    const unit = equipmentList.find((e) => e.id === form.equipment_id);
    saveMutation.mutate({
      equipment_id: form.equipment_id,
      unit_number: unit?.unit_number || unit?.name || '',
      service_date: form.service_date,
      maintenance_type: form.maintenance_type || null,
      status: form.status,
      // From an inspection with nothing typed: no key, so the API names the
      // failed items. Otherwise as before.
      description: inspectionSubmissionId && !form.description.trim() ? undefined : (form.description || null),
      performed_by: form.performed_by || null,
      vendor: form.vendor || null,
      meter_reading: form.meter_reading ? Number(form.meter_reading) : null,
      total_cost: form.total_cost ? Number(form.total_cost) : 0,
      // NO KEY when empty — the API totals the parts lines. A 0 here was a
      // real figure to the API and stopped the total being taken.
      parts_cost: optionalField(form.parts_cost ? Number(form.parts_cost) : null, isEdit && record?.parts_cost != null),
      labor_cost: form.labor_cost ? Number(form.labor_cost) : 0,
      misc_cost: form.misc_cost ? Number(form.misc_cost) : 0,
      // NO KEY when empty — the API measures the out-of-service window. An
      // explicit null on an edit blocked that, so only a clear sends one.
      downtime_hours: optionalField(form.downtime_hours ? Number(form.downtime_hours) : null, isEdit && record?.downtime_hours != null),
      next_service_due: form.next_service_due || null,
      notes: form.notes || null,
      receipt_key: receiptPhotos[0]?.key || form.receipt_key || null,
      ocr_raw: form.ocr_raw || null,
      cause: optionalField(form.cause || null, isEdit && !!record?.cause),
      failed_component: optionalField(form.failed_component.trim() || null, isEdit && !!record?.failed_component),
      parts: optionalField(parts.length > 0 ? parts : null, isEdit && hadParts),
      out_of_service_from: windowField('out_of_service_from', oosFrom),
      returned_to_service_at: windowField('returned_to_service_at', backAt),
      ...(!isEdit && inspectionSubmissionId ? { inspection_submission_id: inspectionSubmissionId } : {}),
    });
  };

  const handleReceiptUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setOcrLoading(true);
    try {
      const formData = new FormData();
      // The route reads `body['receipt']` (asset-expenses.routes.ts) and 400s
      // with "receipt file is required" on any other key. This said 'image', so
      // every upload from this dialog failed; the toast blamed the extraction.
      formData.append('receipt', file);
      const res = await api.assetExpenses.ocr(formData);
      const data: OcrResult = res.data?.data ?? res.data;
      setOcrResult(data);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'OCR failed — enter details manually');
    } finally {
      setOcrLoading(false);
    }
  };

  const applyOcr = useCallback((ocr: OcrResult) => {
    setForm((prev) => ({
      ...prev,
      vendor: ocr.vendor || prev.vendor,
      service_date: ocr.date || prev.service_date,
      total_cost: ocr.total ? String(ocr.total) : prev.total_cost,
      ocr_raw: ocr,
    }));
    setOcrResult(null);
    toast.success('OCR data applied to form');
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 animate-fade-in overflow-y-auto py-8">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-2xl mx-4 shadow-xl">
        {/* Dialog header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-fieldiq-border">
          <h3 className="text-lg font-bold text-foreground">
            {isEdit ? 'Edit Maintenance Record' : 'Add Maintenance Record'}
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Dialog body */}
        <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Receipt OCR */}
          <div className="bg-fieldiq-dark/50 border border-dashed border-fieldiq-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Upload className="w-5 h-5 text-fieldiq-accent" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Upload Receipt (optional)</p>
                <p className="text-xs text-muted-foreground">
                  Upload a receipt image to auto-fill vendor, date, and cost via OCR
                </p>
              </div>
              <label className="cursor-pointer bg-fieldiq-accent/15 text-fieldiq-accent px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-fieldiq-accent/25 transition-colors">
                {ocrLoading ? (
                  <span className="flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Scanning...
                  </span>
                ) : (
                  'Choose File'
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleReceiptUpload}
                  disabled={ocrLoading}
                />
              </label>
            </div>
          </div>

          {/* OCR Review */}
          {ocrResult && (
            <OcrReviewPanel
              ocrResult={ocrResult}
              onAccept={applyOcr}
              onCancel={() => setOcrResult(null)}
            />
          )}

          {/* Equipment selector — hidden when the unit is preselected */}
          {!preselectedEquipmentId && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Equipment Unit *
              </label>
              <select
                value={form.equipment_id}
                onChange={(e) => updateField('equipment_id', e.target.value)}
                className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
              >
                <option value="">Select unit...</option>
                {equipmentList.map((eq) => (
                  <option key={eq.id} value={eq.id}>
                    {eq.unit_number || eq.name} {eq.type ? `(${eq.type})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Row: date + type + status */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Service Date *
              </label>
              <input
                type="date"
                value={form.service_date}
                onChange={(e) => updateField('service_date', e.target.value)}
                className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Type</label>
              <select
                value={form.maintenance_type}
                onChange={(e) => updateField('maintenance_type', e.target.value)}
                className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
              >
                <option value="">Select type...</option>
                {MAINTENANCE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => updateField('status', e.target.value)}
                className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s.replace('_', ' ').replace(/^\w/, (c) => c.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50 resize-none"
            />
          </div>

          {/* Row: performed_by + vendor */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Performed By</label>
              <input
                type="text"
                value={form.performed_by}
                onChange={(e) => updateField('performed_by', e.target.value)}
                className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                placeholder="Name or team"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Vendor</label>
              <input
                type="text"
                value={form.vendor}
                onChange={(e) => updateField('vendor', e.target.value)}
                className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                placeholder="External vendor"
              />
            </div>
          </div>

          {/* Row: meter_reading + downtime_hours */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Meter Reading ({distanceUnit} / hrs)</label>
              <input
                type="number"
                value={form.meter_reading}
                onChange={(e) => updateField('meter_reading', e.target.value)}
                className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                placeholder={`Hours / ${distanceUnit}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Downtime (hrs)</label>
              <input
                type="number"
                value={form.downtime_hours}
                onChange={(e) => updateField('downtime_hours', e.target.value)}
                className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                placeholder="From the out-of-service window"
              />
            </div>
          </div>

          {inspectionSubmissionId && !isEdit && (
            <p className="text-xs text-fieldiq-accent bg-fieldiq-accent/10 rounded-lg px-3 py-2">
              Logging a repair from an inspection. Leave the description empty and it is filled from the
              items that failed, with the cause set to Inspection finding.
            </p>
          )}

          {/* Repair log (api#979) */}
          <div className="bg-fieldiq-dark/50 border border-fieldiq-border rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Repair Log</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="repair-cause" className="block text-xs font-medium text-muted-foreground mb-1">Cause</label>
                <select
                  id="repair-cause"
                  value={form.cause}
                  onChange={(e) => updateField('cause', e.target.value)}
                  className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                >
                  <option value="">Not recorded</option>
                  {REPAIR_CAUSES.map((c) => (
                    <option key={c} value={c}>{REPAIR_CAUSE_LABELS[c]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="repair-failed-component" className="block text-xs font-medium text-muted-foreground mb-1">Failed component</label>
                <input
                  id="repair-failed-component"
                  type="text"
                  value={form.failed_component}
                  onChange={(e) => updateField('failed_component', e.target.value)}
                  className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                  placeholder="e.g. jaw die, latch spring"
                />
              </div>
              <div>
                <label htmlFor="repair-oos-from" className="block text-xs font-medium text-muted-foreground mb-1">Out of service from</label>
                <input
                  id="repair-oos-from"
                  type="datetime-local"
                  value={form.out_of_service_from}
                  onChange={(e) => updateField('out_of_service_from', e.target.value)}
                  className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                />
              </div>
              <div>
                <label htmlFor="repair-back-at" className="block text-xs font-medium text-muted-foreground mb-1">Returned to service</label>
                <input
                  id="repair-back-at"
                  type="datetime-local"
                  value={form.returned_to_service_at}
                  onChange={(e) => updateField('returned_to_service_at', e.target.value)}
                  className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                />
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Parts</p>
              {form.parts.map((p, i) => (
                <div key={i} role="group" aria-label={`Part line ${i + 1}`} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    aria-label="Part"
                    value={p.part}
                    onChange={(e) => updateField('parts', form.parts.map((x, j) => (j === i ? { ...x, part: e.target.value } : x)))}
                    placeholder="Part"
                    className="col-span-4 h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm"
                  />
                  <input
                    aria-label="Part number"
                    value={p.part_number}
                    onChange={(e) => updateField('parts', form.parts.map((x, j) => (j === i ? { ...x, part_number: e.target.value } : x)))}
                    placeholder="Part #"
                    className="col-span-3 h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm"
                  />
                  <input
                    aria-label="Quantity"
                    type="number"
                    min={0}
                    step="any"
                    onWheel={blurOnWheel}
                    value={p.qty}
                    onChange={(e) => updateField('parts', form.parts.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
                    placeholder="Qty"
                    className="col-span-2 h-10 px-2 bg-background border border-border rounded-lg text-foreground text-sm"
                  />
                  <input
                    aria-label="Unit cost"
                    type="number"
                    min={0}
                    step="0.01"
                    onWheel={blurOnWheel}
                    value={p.unit_cost}
                    onChange={(e) => updateField('parts', form.parts.map((x, j) => (j === i ? { ...x, unit_cost: e.target.value } : x)))}
                    placeholder="Unit $"
                    className="col-span-2 h-10 px-2 bg-background border border-border rounded-lg text-foreground text-sm"
                  />
                  <button
                    type="button"
                    aria-label={`Remove part line ${i + 1}`}
                    onClick={() => updateField('parts', form.parts.filter((_, j) => j !== i))}
                    className="col-span-1 h-10 flex items-center justify-center text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => updateField('parts', [...form.parts, emptyPart()])}
                className="text-xs font-semibold text-fieldiq-accent hover:underline"
              >
                + Add part
              </button>
            </div>
          </div>

          {/* Costs */}
          <div className="bg-fieldiq-dark/50 border border-fieldiq-border rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              Cost Breakdown
            </p>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Total Cost *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={form.total_cost}
                  onChange={(e) => updateField('total_cost', e.target.value)}
                  className="w-full h-10 pl-7 pr-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Parts</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={form.parts_cost}
                    onChange={(e) => updateField('parts_cost', e.target.value)}
                    className="w-full h-10 pl-7 pr-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                    placeholder="From parts lines"
                    aria-label="Parts cost"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Labor</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={form.labor_cost}
                    onChange={(e) => updateField('labor_cost', e.target.value)}
                    className="w-full h-10 pl-7 pr-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Misc</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={form.misc_cost}
                    onChange={(e) => updateField('misc_cost', e.target.value)}
                    className="w-full h-10 pl-7 pr-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Next service due */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Next Service Due</label>
            <input
              type="date"
              value={form.next_service_due}
              onChange={(e) => updateField('next_service_due', e.target.value)}
              className="w-full h-10 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50 resize-none"
            />
          </div>

          {/* Receipt photo upload */}
          {(record?.id || form.equipment_id) && (
            <PhotoUploader
              entityType="maintenance"
              entityId={record?.id || form.equipment_id || 'new'}
              value={receiptPhotos}
              onChange={(photos) => {
                setReceiptPhotos(photos);
                if (photos.length > 0) {
                  updateField('receipt_key', photos[0].key);
                }
              }}
              maxPhotos={1}
              label="Receipt Photo"
              freeform
            />
          )}
        </div>

        {/* Dialog footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-fieldiq-border gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            {isEdit && renderFooterExtra?.(record!)}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-fieldiq-border text-muted-foreground rounded-lg text-sm font-medium hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saveMutation.isPending}
              className="flex items-center gap-2 bg-fieldiq-accent text-fieldiq-dark font-semibold px-5 py-2 rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
            >
              {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Record'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
